// download.ts
// -----------
// Shared helper for AUTHENTICATED file downloads (Sprint 121, ADR-114).
//
// THE BUG THIS FIXES
// ------------------
// Since Sprint 110 the access token is held IN MEMORY only (a closure in
// httpClient.ts) — it is never in localStorage and never on a cookie the
// browser will replay. A download triggered by a browser-native navigation
//   • `window.open(url)`
//   • an anchor `href` / `<a download>`
//   • `location.assign(url)`
// is a fresh top-level GET issued by the browser, NOT by our fetch wrapper, so
// it carries NO `Authorization: Bearer` header. The backend sees an
// unauthenticated request → 403 "Full authentication is required" → and the UI
// shows nothing (silent failure). This is a CLASS bug: every authed native
// download is affected.
//
// THE FIX
// -------
// Fetch the resource through the same `apiFetch` plumbing every other API call
// uses (bearer attached, credentials/CSRF as needed, 401→single-flight
// refresh→retry), read the response as a Blob, then trigger a client-side save
// with `URL.createObjectURL` + a temporary `<a download>`. The bytes ride an
// authenticated XHR; only the final save is browser-native (and a blob: URL
// needs no auth).
//
// On a non-2xx the helper THROWS an ApiError (same shape `apiJson` throws), so
// callers can `catch (err)` and toast `err.message`. It never silently resolves
// on failure.

import { apiFetch, parseProblemDetail, ApiError, ProblemInfo } from '../services/httpClient';

export interface DownloadAuthedOptions {
  /** Fallback filename if the server sends no Content-Disposition. */
  filename?: string;
  /** Extra fetch options (method/headers/etc.). GET by default. */
  init?: RequestInit;
  /**
   * When true (default), open the blob in a NEW TAB instead of saving it. Used
   * by "View" affordances. Falls back to a save if the popup is blocked.
   */
  openInNewTab?: boolean;
}

/**
 * Parse a `Content-Disposition` header for its filename. Handles both the
 * RFC-5987 `filename*=UTF-8''...` form and the plain `filename="..."` form.
 * Returns undefined when no usable filename is present.
 */
export function filenameFromContentDisposition(header: string | null): string | undefined {
  if (!header) return undefined;
  // RFC 5987 extended form takes precedence (it carries the real UTF-8 name).
  const star = header.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
  if (star && star[1]) {
    try { return decodeURIComponent(star[1].trim().replace(/^["']|["']$/g, '')); }
    catch { /* fall through to plain form */ }
  }
  const plain = header.match(/filename="?([^";]+)"?/i);
  if (plain && plain[1]) return plain[1].trim();
  return undefined;
}

/** True when a failed response is a feature-gate / policy denial, not missing-auth. */
export function isFeatureGated(problem: ProblemInfo | undefined): boolean {
  if (!problem) return false;
  if (problem.status !== 403) return false;
  // A 403 AFTER we've attached a valid bearer is an access-denied/policy
  // decision (e.g. the feature flag is off) rather than missing-auth — the
  // refresh-retry in apiFetch already handled the genuine no-token (401) case.
  // The backend's AccessDeniedHandler emits "Access Denied"/"Forbidden"; a
  // truly unauthenticated request would have been a 401. Treat any surviving
  // 403 here as a gate so the toast can say "not enabled".
  return true;
}

const FEATURE_GATED_MESSAGE = "This feature isn't enabled for your condo.";

/**
 * Read a failed Response into an ApiError, mapping a surviving 403 to a clear
 * "feature isn't enabled" message and everything else to the extracted
 * ProblemDetail message (Sprint 117).
 */
async function errorFromResponse(response: Response): Promise<ApiError> {
  const body = await response.json().catch(() => null);
  const { message, problem } = parseProblemDetail(response.status, body);
  const finalMessage = isFeatureGated(problem) ? FEATURE_GATED_MESSAGE : message;
  const requestId = response.headers.get('X-Request-Id');
  const tail = requestId ? ` (id: ${requestId})` : '';
  const error = new Error(finalMessage + tail) as ApiError;
  error.problem = problem;
  return error;
}

/** Click a temporary anchor to save (or, with target=_blank, open) a blob URL. */
function saveBlob(blob: Blob, filename: string, openInNewTab: boolean): void {
  const url = URL.createObjectURL(blob);
  try {
    if (openInNewTab) {
      const win = window.open(url, '_blank');
      if (win) {
        // Give the new tab time to load the blob before revoking.
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        return;
      }
      // Popup blocked → fall through to a save so the user still gets the file.
    }
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    if (!openInNewTab) URL.revokeObjectURL(url);
  }
}

/**
 * Download (or open) an authenticated resource as a file.
 *
 * @param path     API path WITHOUT the `/api` base (apiFetch prepends it),
 *                 e.g. `/reports/condo/{id}/pdf/ytd`.
 * @param opts     filename fallback, fetch init, openInNewTab.
 *
 * Resolves once the save/open has been triggered. Throws an {@link ApiError}
 * (with `.message` and `.problem`) on any non-2xx so the caller can toast it.
 */
export async function downloadAuthed(
  path: string,
  opts: DownloadAuthedOptions = {},
): Promise<void> {
  const response = await apiFetch(path, { ...(opts.init ?? {}) });
  if (!response.ok) {
    throw await errorFromResponse(response);
  }
  const blob = await response.blob();
  const serverName = filenameFromContentDisposition(
    response.headers.get('Content-Disposition'),
  );
  const filename = serverName || opts.filename || 'download';
  saveBlob(blob, filename, opts.openInNewTab ?? false);
}

/**
 * Fetch an authenticated resource and RETURN its bytes as a Blob (rather than
 * saving/opening it). Used when the caller needs the blob in memory — e.g. the
 * status-certificate wizard renders the server PDF into a preview modal.
 *
 * Same auth plumbing as {@link downloadAuthed} (bearer attached, 401→refresh→retry);
 * throws an {@link ApiError} on any non-2xx so the caller can toast `.message`.
 */
export async function fetchAuthedBlob(
  path: string,
  init: RequestInit = {},
): Promise<Blob> {
  const response = await apiFetch(path, { ...init });
  if (!response.ok) {
    throw await errorFromResponse(response);
  }
  return response.blob();
}
